<script lang="ts">
	import { isDateValid, parseDateISO } from '$lib/utils/date';
	import type {
		TableDataRowProps,
		TableProps,
		TableFiltersValues,
		TableDataCellProps
	} from './types';
	import Icon from '../../elements/Icon.svelte';
	import { attrValue } from '$lib/utils/html';
	import { undefOrNull } from '$lib/utils/objects';
	import { t } from 'svelte-i18n';
	import DynamicComponent from '$lib/components/DynamicComponent.svelte';
	import { viewport } from '$lib/stores';
	import TabletSwipeableWrapper from '@app/components/layouts/TabletSwipeableWrapper.svelte';
	import { formatAmountWithCurrency } from '$lib/utils/strings';
	import { onMount } from 'svelte';
	import type { InputSelectProps, InputTextProps } from '$lib/elements/inputs/types';
	import { bannersHeight } from '@app/stores/banners';
	import { headerHeight } from '@app/stores/header';
	import Hint from '@app/lib/elements/Hint.svelte';
	import FilterControls from './FilterControls.svelte';
	import { buildFilters } from '@app/utils/table';
	import { stickyAction } from '$lib/actions';
	import ButtonBoxTextColor from '$lib/elements/buttons/variants/ButtonBoxTextColor.svelte';

	export let props: TableProps = {
		headers: {
			data: []
		},
		data: []
	};
	export let withRowsBorder = false;
	export let withLoadMoreActions = false;
	export let withSearch = false;
	export let columnIndexToSearch: number;
	export let searchPlaceholder: string;
	export let initialNumberDisplay = 20;
	export let sticky = false;
	export let top = undefined;

	let originalPropsData: TableDataRowProps[] | undefined;
	let filters: InputSelectProps[] | undefined;
	let searchProps: InputTextProps = {
		placeholder: searchPlaceholder ?? $t('projectsPage.searchPlaceholder')
	};
	let showLoadMoreButton: boolean =
		withLoadMoreActions && props?.data && initialNumberDisplay < props.data.length;

	let showLoadLessButton: boolean = false;
	let isStuck = false;

	function handleStuck(e) {
		isStuck = e.detail.isStuck;
	}

	const initSortOptions = () => {
		const defaultSortedIndex = props.headers?.data.findIndex((e) => e?.isDefaultKey) ?? undefined;
		return {
			sortColIndex:
				defaultSortedIndex != undefined && defaultSortedIndex >= 0 ? defaultSortedIndex : null,
			dir:
				defaultSortedIndex && props.headers?.data[defaultSortedIndex]?.dir
					? props.headers?.data[defaultSortedIndex]?.dir
					: 'desc'
		};
	};

	const resetFilters = () => {
		activeFiltersValues = [];
		hideLines();
		filters = filters.map((f) => {
			if (f) {
				f.value = undefined;
			}
			return f;
		});
	};

	$: if (searchProps.value && originalPropsData) {
		filters && resetFilters();
		props.data = originalPropsData.filter((e) =>
			e?.cells[columnIndexToSearch]?.data?.value
				.toString()
				.toLowerCase()
				.includes(searchProps.value.toString().toLowerCase())
		);
		props.data.forEach((e) => {
			e.isHidden = false;
			e.isHiddenWithLoadMore = false;
		});
		if (props.footer) {
			props.footer[0].isHidden = true;
		}
	} else if (!searchProps.value && originalPropsData) {
		props.data = originalPropsData;
		props.footer && (props.footer[0].isHidden = false);
	}

	let footer: TableDataRowProps[] = [];
	$: footer = props.footer ?? [];
	let sortOptions = initSortOptions();

	const getNextDir = () => {
		switch (sortOptions.dir) {
			case 'asc':
				return 'desc';
			case 'desc':
			default:
				return 'asc';
		}
	};

	const compareValues = (
		v1: string | number | boolean,
		v2: string | number | boolean,
		dir: 'asc' | 'desc' | undefined | null
	) => {
		if (typeof v1 == 'string' && isDateValid(v1) && typeof v2 == 'string' && isDateValid(v2)) {
			v1 = parseDateISO(v1).getTime();
			v2 = parseDateISO(v2).getTime();
		}
		if (undefOrNull(v1)) {
			return 1;
		} else if (undefOrNull(v2)) {
			return -1;
		}
		if (dir === 'asc') {
			return v1 < v2 ? -1 : v1 > v2 ? 1 : 0;
		} else if (dir === 'desc') {
			return v1 > v2 ? -1 : v1 < v2 ? 1 : 0;
		} else {
			return 0;
		}
	};

	const updateTableWithLoadMoreAction = () => {
		props.data = props?.data?.map((line, index) => {
			line.isHiddenWithLoadMore = index > initialNumberDisplay;
			return line;
		});
		updateSum();
		filters = buildFilters(props, $t);
	};

	const loadAll = () => {
		showLoadMoreButton = false;
		showLoadLessButton = true;
		initialNumberDisplay = props.data?.length ?? 0;
		updateTableWithLoadMoreAction();
	};
	const loadLess = () => {
		showLoadMoreButton = true;
		showLoadLessButton = false;
		initialNumberDisplay = 20;
		updateTableWithLoadMoreAction();
	};

	const sortTable = () => {
		sortOptions.sortColIndex !== null &&
			(props.data =
				props.data?.sort((a, b) =>
					compareValues(
						a.cells[sortOptions.sortColIndex].data.value,
						b.cells[sortOptions.sortColIndex].data.value,
						sortOptions.dir
					)
				) ?? []);
	};

	if (sortOptions.sortColIndex !== null) {
		sortTable();
	}

	let activeFiltersValues: TableFiltersValues[] = [];
	const hideLines = () => {
		props.data?.forEach((row) => {
			let numberOfCellValueMatchingActiveFiltersValues = 0;
			activeFiltersValues.forEach((f) => {
				if (row.cells[f.index]?.data.value === f.value) {
					numberOfCellValueMatchingActiveFiltersValues++;
				}
			});
			row.isHidden = numberOfCellValueMatchingActiveFiltersValues !== activeFiltersValues.length;
		});
	};

	const updateSum = () => {
		let sumOfSpans = 0;
		props.footer &&
			props.footer[0].cells.forEach((c, i) => {
				if (c) {
					if (typeof c.data.display == 'string' && c.data.display.includes('€')) {
						let totalValue = props.data
							?.map((row): number => {
								return !row.isHidden && !row.isHiddenWithLoadMore
									? (row.cells[sumOfSpans]?.data.value as number)
									: 0;
							})
							.reduce((prev, next): number => prev + next);

						(totalValue || totalValue === 0) &&
							(c.data.display = formatAmountWithCurrency(totalValue));
						applyColorIndicator(i, c, totalValue);

						sumOfSpans++;
					} else {
						if (c.span) {
							sumOfSpans = sumOfSpans + c.span;
						} else {
							sumOfSpans++;
						}
					}
				}
			});
	};

	const filterTable = (f: InputSelectProps, i: number) => {
		let currentFilterValue;
		currentFilterValue = f.value;

		if (currentFilterValue !== undefined) {
			if (!activeFiltersValues.length) {
				activeFiltersValues.push({ index: i, value: currentFilterValue });
			} else {
				let currentActivatedFilter = activeFiltersValues.find((f) => f.index === i);
				if (currentActivatedFilter) {
					currentActivatedFilter.value = currentFilterValue;
				} else {
					activeFiltersValues.push({ index: i, value: currentFilterValue });
				}
			}
			// filter
		} else {
			// reset
			activeFiltersValues = activeFiltersValues.filter((e) => e.index !== i);
		}

		hideLines();
		updateSum();
		props.footer = props.footer;
		props.data = props.data;
	};

	const applyColorIndicator = (
		i: number,
		c: TableDataCellProps,
		totalValue: number | undefined
	) => {
		if (i !== 1 && c.colorIndicator && (totalValue || totalValue === 0)) {
			c.classNames = `${totalValue > 0 ? 'text-success' : 'text-danger'} font-semibold`;
		}
	};

	onMount(() => {
		if (withLoadMoreActions) {
			updateTableWithLoadMoreAction();
		} else {
			filters = buildFilters(props, $t);
		}
		originalPropsData = props.data;
	});

	$: onSortClick = (index: number) => {
		if (sortOptions.sortColIndex !== index) {
			sortOptions = {
				sortColIndex: index,
				dir: 'asc'
			};
		} else {
			sortOptions = {
				...sortOptions,
				dir: getNextDir()
			};
		}

		sortTable();
	};

	$: isSortOn = (index: number) => {
		return index === sortOptions.sortColIndex;
	};
</script>

{#if filters?.filter((f) => f !== undefined).length || withSearch}
	<FilterControls
		bind:searchProps
		{filters}
		onClickSelect={filterTable}
		{columnIndexToSearch}
		{withSearch}
	/>
{/if}
<TabletSwipeableWrapper classNames="!snap-none">
	<table id={props.id} class={`lg:table-fixed ${props.classNames}`} bind:this={props.ref}>
		{#if props.headers.data}
			<thead>
				<tr class={props.headers.classNames}>
					{#each props.headers.data as header, index}
						{#if header && ((header.isMobile && $viewport.isMobile) || !$viewport.isMobile)}
							<th
								class={header.classNames}
								colspan={header.span ?? 1}
								class:sticky-cell={sticky}
								class:bordered-rows={withRowsBorder}
								style={sticky && top
									? `--sticky-top: ${top[0]}px; --sticky-top-md: ${top[1]}px; --sticky-top-lg: ${top[2]}px; --sticky-top-xl: ${top[3]}px; --sticky-top-2xl: ${top[4]}px`
									: `top:${$bannersHeight + ($viewport.isDesktop ? $headerHeight : 0)}px !important`}
							>
								<div
									class="inline-flex items-center {isSortOn(index)
										? 'text-secondary'
										: 'text-grey-dark'} font-medium"
								>
									{#if header.label}
										<span class="first-letter:uppercase">{@html header.label}</span>
									{/if}
									{#if header.tooltipTitle}
										<Hint tooltip={{ title: header.tooltipTitle }} />
									{/if}
									{#if header.label && header.sortable && props.data && props.data.length > 1}
										<Icon
											name="arrow_down_square"
											size="small"
											classNames={attrValue([
												'ml-3 cursor-pointer',
												isSortOn(index) && sortOptions.dir === 'asc' ? 'transform rotate-180' : ''
											])}
											on:click={() => onSortClick(index)}
										/>
									{/if}
								</div>
							</th>
						{/if}
					{/each}
				</tr>
			</thead>
		{/if}
		<tbody>
			{#if props.data}
				{#each props.data as row}
					<tr
						class={attrValue([row.classNames, row.onClick ? 'cursor-pointer' : ''])}
						class:hidden={row.isHidden || row.isHiddenWithLoadMore}
						on:click={() => {
							if (row.onClick) row.onClick();
						}}
					>
						{#each row.cells as cell, index}
							{#if cell && ((props.headers?.data[index]?.isMobile && $viewport.isMobile) || !$viewport.isMobile)}
								<td
									class={attrValue([cell.classNames])}
									class:td-bordered={withRowsBorder}
									colspan={cell.span ?? 1}
								>
									{#if cell.data.display || typeof cell.data.display === 'boolean'}
										{#if typeof cell.data.display == 'string'}
											{@html cell.data.display}
										{:else if typeof cell.data.display == 'boolean'}
											<Icon
												name={cell.data.display ? 'check_circle' : 'close_circle'}
												classNames={cell.data.display ? 'text-success' : 'text-error'}
											/>
										{:else if typeof cell.data.display == 'object'}
											<DynamicComponent component={cell.data.display} />
										{/if}
									{:else if typeof cell.data.value == 'string' || typeof cell.data.value == 'number'}
										{cell.data.value}
									{/if}
								</td>
							{/if}
						{/each}
					</tr>
				{/each}
			{/if}
			{#each footer as row}
				<tr
					class={attrValue([
						'mx-4 footer-row bg-grey-lighter h-[60px] relative',
						row.isSticky && 'sticky bottom-1',
						row.classNames
					])}
					use:stickyAction={{ earlyReturn: !row.isSticky, stickToTop: false }}
					on:stuck={handleStuck}
					class:isStuck
					class:hidden={row.isHidden}
				>
					{#each row.cells as cell, index}
						{#if cell && ((props.headers?.data[index]?.isMobile && $viewport.isMobile) || !$viewport.isMobile)}
							<td
								class={attrValue([cell.classNames])}
								class:td-bordered={withRowsBorder}
								colspan={cell.span ?? 1}
							>
								{#if cell.data.display}
									{#if typeof cell.data.display == 'string'}
										<div class="first-letter:uppercase inline-block">{@html cell.data.display}</div>
									{:else if typeof cell.data.display === 'boolean'}
										<Icon
											name={cell.data.display ? 'check_circle' : 'close_circle'}
											classNames={cell.data.display ? 'text-success' : 'text-error'}
										/>
									{:else if typeof cell.data.display == 'object'}
										<DynamicComponent component={cell.data.display} />
									{/if}
								{:else if typeof cell.data.value == 'string' || typeof cell.data.value == 'number'}
									<span class="first-letter:uppercase"> {cell.data.value}</span>
								{:else if typeof cell.data.value == 'boolean'}
									<Icon name={cell.data.value ? 'check' : 'cross'} />
								{/if}
								{#if index === 0 && showLoadMoreButton}
									<span class="text-sm">{`(sur ${initialNumberDisplay} lignes)`}</span>
								{/if}
							</td>
						{/if}
					{/each}
				</tr>
			{/each}
		</tbody>
	</table>
</TabletSwipeableWrapper>

{#if !searchProps.value}
	{#if showLoadMoreButton}
		<div class="flex justify-center mt-4 pb-4">
			<ButtonBoxTextColor
				props={{
					label: $t('loadAll'),
					classNames: 'inline-flex'
				}}
				on:click={() => loadAll()}
			/>
		</div>
	{/if}
	{#if showLoadLessButton}
		<div class="flex justify-center mt-4 pb-4">
			<ButtonBoxTextColor
				props={{
					label: $t('loadLess'),
					classNames: 'inline-flex'
				}}
				on:click={() => loadLess()}
			/>
		</div>
	{/if}
{/if}

<style lang="postcss">
	table {
		@apply border-separate w-full;
		border-spacing: 0 theme(spacing.2);
		:global(.hiddenInTableCell) {
			display: none;
		}
		:global(.tag) {
			@apply !text-sm;
		}
	}
	td {
		@apply py-2 text-center lg:py-1 lg:text-sm 2xl:text-base 2xl:py-2;
	}
	td:not(.td-bordered) + td,
	th:not(.bordered-rows) + th {
		@apply pl-3;
	}
	td.td-bordered {
		@apply px-3 lg:px-2 2xl:px-3;
	}
	th {
		z-index: 1;
		@apply text-center pb-3 lg:text-sm 2xl:text-base;
	}
	th.bordered-rows {
		@apply px-3 lg:px-2 2xl:px-3;
	}
	.td-bordered {
		@apply first:border-l border-t last:border-r border-b border-grey;
	}
	.td-bordered:first-child {
		@apply rounded-tl-lg rounded-bl-lg;
	}
	.td-bordered:last-child {
		@apply rounded-tr-lg rounded-br-lg;
	}
	th:first-child,
	td:first-child {
		@apply text-left;
	}
	th:last-child,
	td:last-child {
		@apply text-right;
	}
	tr:not(.footer-row):hover {
		position: relative;
	}
	tr:hover td:not(.td-bordered):first-child,
	.footer-row td:first-child {
		@apply before:block before:absolute before:-left-2 before:bg-grey-lighter before:w-2 before:h-full before:top-0 before:rounded-tl-lg before:rounded-bl-lg;
	}
	tr:hover td:not(.td-bordered):last-child,
	.footer-row td:last-child {
		@apply before:block before:absolute before:-right-2 before:bg-grey-lighter before:w-2 before:h-full before:top-0 before:rounded-tr-lg before:rounded-br-lg;
	}
	.footer-row.isStuck {
		box-shadow: 0 7px 15px 15px theme('colors.grey-light');
		z-index: 10;
	}
	th:last-child,
	td:last-child {
		@apply text-right;
	}

	.sticky-cell {
		@apply bg-white z-[9];
		position: sticky;
		top: var(--sticky-top);
		@screen md {
			top: var(--sticky-top-md);
		}
		@screen lg {
			top: var(--sticky-top-lg);
		}
		@screen xl {
			top: var(--sticky-top-xl);
		}
		@screen 2xl {
			top: var(--sticky-top-2xl);
		}
	}
	tbody tr:hover {
		@apply bg-grey-lighter;
	}
</style>
